import {
	LTI_EXCHANGE_ERROR_CODES,
	type LtiExchangeErrorCode,
	type LtiExchangeResponse,
} from "@elaso/shared/types/lti";

const API_URL = import.meta.env.VITE_SERVER_URL || "http://localhost:3000";

type TokenResponse = {
	token: string;
};

type ErrorResponse = {
	error: string;
};

/** Request a one-time WebSocket authentication token from the server. */
export async function requestAuthToken(
	firebaseIdToken: string
): Promise<string> {
	const response = await fetch(`${API_URL}/auth/token`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ idToken: firebaseIdToken }),
	});

	if (!response.ok) {
		const error: ErrorResponse = await response.json();
		throw new Error(error.error || "Failed to get auth token");
	}

	const data: TokenResponse = await response.json();
	return data.token;
}

/**
 * A failed `/lti/exchange`, carrying the server's code rather than a message.
 *
 * Deliberately unlike the two calls in this file, which throw prose. Nothing the
 * exchange can refuse has a sentence that helps whoever reads it, and the copy
 * for all of them lives in one place, `lib/lti/error-copy.ts`, shared with the
 * failures the launch itself produced.
 */
export class LtiExchangeError extends Error {
	readonly code: LtiExchangeErrorCode;

	constructor(code: LtiExchangeErrorCode) {
		super(`LTI exchange refused: ${code}`);
		this.name = "LtiExchangeError";
		this.code = code;
	}
}

const isExchangeErrorCode = (value: unknown): value is LtiExchangeErrorCode =>
	typeof value === "string" &&
	(LTI_EXCHANGE_ERROR_CODES as readonly string[]).includes(value);

/** Whatever the server said, as long as it is a code we know. */
const readExchangeError = async (
	response: Response
): Promise<LtiExchangeErrorCode> => {
	try {
		const body: unknown = await response.json();
		const code = (body as { code?: unknown } | null)?.code;
		if (isExchangeErrorCode(code)) {
			return code;
		}
	} catch {
		// A non-JSON body from a proxy rather than from us.
	}
	return "internal";
};

/**
 * Trade the one-time handoff code an LTI launch put in the URL fragment for a
 * Firebase custom token (TECH-3184's `POST /lti/exchange`).
 *
 * Single-use and short-lived on the server side, so this must be called exactly
 * once per code. `lib/lti/launch-flow.ts` owns that guarantee, not this function.
 */
export async function exchangeLtiCode(
	code: string
): Promise<LtiExchangeResponse> {
	let response: Response;
	try {
		response = await fetch(`${API_URL}/lti/exchange`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ code }),
		});
	} catch {
		// Offline, DNS and CORS are indistinguishable from here, and all of them
		// mean the same thing to the launcher, that the tool could not be reached.
		throw new LtiExchangeError("exchange_unavailable");
	}

	if (!response.ok) {
		throw new LtiExchangeError(await readExchangeError(response));
	}

	let data: unknown;
	try {
		data = await response.json();
	} catch {
		// A 200 with a body that is not JSON at all, so a proxy's rather than ours.
		// Same meaning as a 200 that misses the contract, and it must arrive as an
		// `LtiExchangeError` either way. A raw `SyntaxError` would reach
		// `completeLtiLaunch` as an unexpected post-exchange failure and be filed to
		// Sentry as a launch bug.
		throw new LtiExchangeError("internal");
	}
	const { customToken, workspaceId } = (data ?? {}) as Partial<
		Record<keyof LtiExchangeResponse, unknown>
	>;
	if (typeof customToken !== "string" || typeof workspaceId !== "string") {
		// A 200 that is not the contract. Refusing beats handing a half-typed
		// object to `signInWithCustomToken`.
		throw new LtiExchangeError("internal");
	}
	return { customToken, workspaceId };
}

export async function requestPasswordReset(email: string): Promise<void> {
	const response = await fetch(`${API_URL}/auth/forgot-password`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ email }),
	});

	// Swallow server-side errors, or the endpoint becomes an email-enumeration oracle.
	await response.text();
}
